'use client'
import { MediaImageStandard } from 'components/Contentful'
import classNames from 'classnames'
import useInactivityStatus from 'hooks/useInactivityStatus'

const AwardCard = ({ award }) => {
  const { eyebrow, title, subtitle, image, eyebrow2, subtitle2, isStat } = award

  // Solo usar diseño de stat si isStat es true explícitamente (para mantener compatibilidad)
  if (isStat === true) {
    return (
      <div className="flex flex-col items-center justify-center w-[310px] sm:w-[363px] min-h-[96px] shrink-0 text-center">
        {title && (
          <div
            className="font-ultramarine text-[40px] md:text-[56px] font-bold leading-[1.2] text-[#1D252F] mb-1"
            data-testid="stat-title"
          >
            {title}
          </div>
        )}
        {subtitle && (
          <div
            className="font-ultramarine text-[14px] md:text-[16px] font-normal leading-[1.4] text-[#5C708A]"
            data-testid="stat-subtitle"
          >
            {subtitle}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="flex items-center gap-4 w-[310px] sm:w-[363px] min-h-[96px] shrink-0">
      {image && (
        <div className="shrink-0 w-[80px] min-h-[96px] md:h-[53px] flex items-center justify-center">
          <MediaImageStandard
            content={image}
            layout="fixed"
            width={image?.width || 116}
            height={image?.height || 53}
            alt={image?.title || 'Award'}
            fetchPriority="high"
            preload={true}
          />
        </div>
      )}

      <div className="flex flex-col flex-1 gap-1 min-w-0" data-testid="award-item">
        {eyebrow && (
          <>
            <div
              className="uppercase font-ultramarine text-[12px] md:text-[14px] font-bold leading-[120%] tracking-[2px] md:tracking-[3.5px] text-[#00A86B]"
              data-testid="award-eyebrow"
            >
              {eyebrow}
            </div>
            <div
              className="uppercase font-ultramarine text-[12px] md:text-[14px] font-bold leading-[120%] tracking-[2px] md:tracking-[3.5px] text-[#00A86B]"
              data-testid="award-eyebrow2"
            >
              {eyebrow2}
            </div>
          </>
        )}
        {title && (
          <div
            className="font-ultramarine text-[14px] md:text-[16px] font-bold text-[#1D252F]"
            data-testid="award-title"
          >
            {title}
          </div>
        )}
        {subtitle && (
          <div
            className="font-ultramarine text-[11px] text-[14px] font-normal leading-[1.4] text-[#5C708A]"
            data-testid="award-subtitle"
          >
            {subtitle} <b>{subtitle2}</b>
          </div>
        )}
      </div>
    </div>
  )
}

const LogoItem = ({ logo }) => {
  return (
    <div
      className="flex flex-col justify-center items-center w-[116px] h-[53px] shrink-0 grayscale hover:grayscale-0 transition-all duration-300"
      style={{ padding: '2.831px 1px 10.83px 0', gap: '2.831px' }}
    >
      <MediaImageStandard
        content={logo}
        layout="fixed"
        width={logo?.width || 116}
        height={logo?.height || 53}
        alt={logo?.title || 'Logo'}
        fetchPriority="high"
        preload={true}
      />
    </div>
  )
}

export default function HomeTrustMarkerV2Client({
  awards = [],
  logos = [],
  sectionClassName = 'bg-white py-12 lg:py-16',
  containerClassName = 'container mx-auto px-2 md:px-0 lg:px-0',
  awardsClassName = '',
}) {
  const isUserActive = useInactivityStatus()

  // Duplicate arrays for seamless infinite scroll
  const doubledAwards = [...awards, ...awards]
  const doubledLogos = [...logos, ...logos]

  return (
    <section className={sectionClassName} data-testid="home-trust-market-v2">
      {/* Below md the container's small breakpoint steps (320px/480px) would cap the marquees'
          width and leave wide side gutters; use the full width with a 1em gutter instead. */}
      <div className={classNames(containerClassName, 'max-md:max-w-none max-md:px-[1em]')}>
        {awards.length > 0 && (
          <div className={classNames('mb-4', awardsClassName)}>
            <div
              className="hidden lg:flex justify-center gap-[23px]"
              data-testid="award-item-container"
            >
              {awards.map((award, index) => (
                <AwardCard key={`award-desktop-${index}`} award={award} />
              ))}
            </div>

            <div className="hidden md:grid md:grid-cols-2 lg:hidden gap-8">
              {awards.slice(0, 2).map((award, index) => (
                <AwardCard key={`award-tablet-${index}`} award={award} />
              ))}
            </div>

            <div className="md:hidden overflow-hidden w-full py-4">
              <div
                className="flex gap-[23px] items-center hover:[animation-play-state:paused]"
                style={{
                  width: 'max-content',
                  animation: isUserActive ? 'scroll-loop 30s linear infinite' : 'none',
                }}
              >
                {doubledAwards.map((award, index) => (
                  <div key={`award-${index}`} className="shrink-0">
                    <AwardCard award={award} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {logos.length > 0 && (
          <>
            <div className="md:hidden overflow-hidden w-full py-4 border-t border-gray-200">
              <div
                className="flex gap-[23px] items-center hover:[animation-play-state:paused]"
                style={{
                  width: 'max-content',
                  animation: isUserActive ? 'scroll-loop 40s linear infinite' : 'none',
                }}
              >
                {doubledLogos.map((logo, index) => (
                  <div key={`logo-${index}`} className="shrink-0 w-[120px]">
                    <LogoItem logo={logo} />
                  </div>
                ))}
              </div>
            </div>

            <div className="hidden md:block border-t border-gray-200 pt-[23px]">
              <div
                className="flex flex-wrap justify-center items-center gap-2"
                data-testid="logo-item-container"
              >
                {logos.map((logo, index) => (
                  <LogoItem key={`logo-desktop-${index}`} logo={logo} />
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  )
}
